"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ZebecCardService = void 0;
const bignumber_js_1 = __importDefault(require("bignumber.js"));
const buffer_1 = require("buffer");
const anchor_1 = require("@coral-xyz/anchor");
const web3_js_1 = require("@solana/web3.js");
const constants_1 = require("./constants");
const payload_1 = require("./payload");
const utils_1 = require("./utils");
/**
 * Zebec card service for interacting with zebec instant card program
 *
 * @example
 *
 * ```
 * const connection = new Connection(process.env.RPC_URL || clusterApiUrl("mainnet-beta"));
 * const wallet = useWallet();
 * const provider = getAnchorProvider(connection, wallet);
 * const program = ZebecCardProgramFactory.getProgram(provider);
 * const instructions = new ZebecCardInstructions(program);
 * const service = new ZebecCardService(instructions, connection, wallet.signTranaction);
 *
 * // or
 * const provider = getZebecConnectionProvider(connection);
 * const program = ZebecCardProgramFactory.getProgram(provider);
 * const instructions = new ZebecCardInstructions(program);
 * const service = new ZebecCardService(instructions, connection);
 * ```
 */
class ZebecCardService {
    static deriveCardPda(programId) {
        const _programId = new web3_js_1.PublicKey(programId);
        const [cardPda] = web3_js_1.PublicKey.findProgramAddressSync([buffer_1.Buffer.from(anchor_1.utils.bytes.utf8.encode(constants_1.PROGRAM_SEEDS.cardPdaSeed))], _programId);
        return cardPda;
    }
    /**
     * Derive Buyer Pda
     * @param buyerAddress Card buyer | Depositor
     * @param programId Zebec instant card program id
     * @param buyerCounter counter to create unique buyerPda
     * @returns
     */
    static deriveBuyerPda(buyerAddress, programId, buyerCounter) {
        const buyer = new web3_js_1.PublicKey(buyerAddress);
        const _programId = new web3_js_1.PublicKey(programId);
        const [buyerPda] = web3_js_1.PublicKey.findProgramAddressSync([
            buffer_1.Buffer.from(anchor_1.utils.bytes.utf8.encode(constants_1.PROGRAM_SEEDS.buyerPdaSeed)),
            buyer.toBuffer(),
            new anchor_1.BN(buyerCounter).toArrayLike(buffer_1.Buffer, "le", 8),
        ], _programId);
        return buyerPda;
    }
    /**
     *
     * @param instructions Zebec instant card instruction factory
     * @param _connection Solana web3 connection
     * @param _signTransaction Optional: A function that signs and return signed transaction
     */
    constructor(instructions, _connection, _signTransaction) {
        this.instructions = instructions;
        this._connection = _connection;
        this._signTransaction = _signTransaction;
        this._programId = this.instructions.program.programId;
    }
    _createPayload(payerKey, instructions, signers) {
        return __awaiter(this, void 0, void 0, function* () {
            const { blockhash, lastValidBlockHeight } = yield this._connection.getLatestBlockhash();
            const message = new web3_js_1.TransactionMessage({
                instructions,
                payerKey,
                recentBlockhash: blockhash,
            }).compileToV0Message();
            const transaction = new web3_js_1.VersionedTransaction(message);
            const errorMap = new Map();
            this.instructions.program.idl.errors.forEach((error) => errorMap.set(error.code, error.msg));
            return new payload_1.TransactionPayload(this._connection, errorMap, blockhash, lastValidBlockHeight, transaction, signers, this._signTransaction);
        });
    }
    /**
     * Create transaction payload to initialize zebec card pda and config data
     * @param params Init card config params
     * @example
     *
     * ```
     * const params: InitCardConfigParams = {
     *		revenueFee: 2.5,
     *		solFee: 5,
     *		tokenFee: 10,
     *		zicOwnerAddress: provider.publicKey.toString(),
     *		cardVaultAddress: "FeSXhyQqqGYjrsUyetNdKvV2sMbXHNHRxYRrkSaWmb8N",
     *		revenueVaultAddress: "5tWt4nDbv8TdZUuEuDNauHQC7fGU2GSQ6t2RvcKxuAK3",
     *	}
     * const payload = await service.initCardConfig(params);
     * const signature = await payload.execute({ commitment: "finalized" });
     * ```
     */
    initCardConfig(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const { revenueFee, solFee, tokenFee, zicOwnerAddress, cardVaultAddress, revenueVaultAddress, usdcAddress, } = params;
            const zicOwner = new web3_js_1.PublicKey(zicOwnerAddress);
            const usdcToken = new web3_js_1.PublicKey(usdcAddress);
            const cardVault = new web3_js_1.PublicKey(cardVaultAddress);
            const revenueVault = new web3_js_1.PublicKey(revenueVaultAddress);
            const cardPda = ZebecCardService.deriveCardPda(this._programId);
            const ixs = [];
            const ix = yield this.instructions.getInitCardConfigsInstruction(cardPda, usdcToken, zicOwner, {
                revenueVault,
                cardVault,
                revenueFee,
                solFee,
                tokenFee,
            });
            ixs.push(ix);
            const cardVaultUsdcAccount = (0, utils_1.getAssociatedTokenAddressSync)(usdcToken, cardVault, true);
            const cardVaultUsdcAccountInfo = yield this._connection.getAccountInfo(cardVaultUsdcAccount, "confirmed");
            if (!cardVaultUsdcAccountInfo) {
                ixs.push((0, utils_1.createAssociatedTokenAccountInstruction)(zicOwner, cardVaultUsdcAccount, cardVault, usdcToken));
            }
            const revenueVaultUsdcAccount = (0, utils_1.getAssociatedTokenAddressSync)(usdcToken, revenueVault, true);
            const revenueVaultUsdcAccountInfo = yield this._connection.getAccountInfo(revenueVaultUsdcAccount, "confirmed");
            if (!revenueVaultUsdcAccountInfo) {
                ixs.push((0, utils_1.createAssociatedTokenAccountInstruction)(zicOwner, revenueVaultUsdcAccount, revenueVault, usdcToken));
            }
            const payload = yield this._createPayload(zicOwner, ixs, []);
            return payload;
        });
    }
    /**
     * Create transaction payload to update zebec card config data
     * @param params Set card config params
     * @example
     *
     * ```
     * const params: SetCardConfigParams = {
     *		revenueFee: 2.5,
     *		solFee: 5,
     *		tokenFee: 10,
     *		zicOwnerAddress: provider.publicKey.toString(),
     *		cardVaultAddress: "FeSXhyQqqGYjrsUyetNdKvV2sMbXHNHRxYRrkSaWmb8N",
     *		revenueVaultAddress: "5tWt4nDbv8TdZUuEuDNauHQC7fGU2GSQ6t2RvcKxuAK3",
     *	}
     * const payload = await service.setCardConfig(params);
     * const signature = await payload.execute({ commitment: "finalized" });
     * ```
     */
    setCardConfig(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const { revenueFee, solFee, tokenFee, zicOwnerAddress, cardVaultAddress, revenueVaultAddress } = params;
            const zicOwner = new web3_js_1.PublicKey(zicOwnerAddress);
            const cardPda = ZebecCardService.deriveCardPda(this._programId);
            const cardVault = new web3_js_1.PublicKey(cardVaultAddress);
            const revenueVault = new web3_js_1.PublicKey(revenueVaultAddress);
            const ix = yield this.instructions.getSetCardConfigsInstruction(cardPda, zicOwner, {
                revenueFee,
                solFee,
                tokenFee,
                cardVault,
                revenueVault,
            });
            const payload = yield this._createPayload(zicOwner, [ix], []);
            return payload;
        });
    }
    /**
     * Create transaction payload that swaps given input input mint equivalent to given input amount to USDC and deposit that amount to card vault and revenue vault.
     * @param params SwapAndDepositParams
     * @example
     *
     * ```
     * const buyerCounter = await service.getNextBuyerCounter();
     * const params: SwapAndDepositParams = {
     *     buyerAddress: provider.publicKey.toString(),
     *	   buyerCounter,
     * 	   cardType: "master card",
     *	   inputAmount: 100,
     *	   inputMintAddress: "zebeczgi5fSEtbpfQKVZKCJ3WgYXxjkMUkNNx7fLKAF",
     *	   slippagePercent: 0.5,
     *	   tokenType: "volatile",
     * };
     * const payload = await service.swapAndDeposit(params);
     * const signature = await service.execute({ commitment: "finalized" });
     * ```
     */
    swapAndDeposit(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const { buyerAddress, buyerCounter, cardType, inputMintAddress, inputAmount, slippagePercent, tokenType, outputMintAddress, } = params;
            const buyer = new web3_js_1.PublicKey(buyerAddress);
            const inputMint = new web3_js_1.PublicKey(inputMintAddress);
            const usdc = new web3_js_1.PublicKey(outputMintAddress);
            const inputAmt = (0, bignumber_js_1.default)(inputAmount);
            const parsedInputAmount = inputAmt.times(constants_1.TEN_BIGNUM.pow(constants_1.USDC_DECIMALS));
            const slippage = (0, utils_1.percentToBps)(slippagePercent);
            const buyerInputMintAta = (0, utils_1.getAssociatedTokenAddressSync)(inputMint, buyer);
            const buyerInputMintAtaInfo = yield this._connection.getAccountInfo(buyerInputMintAta, "confirmed");
            if (!buyerInputMintAtaInfo) {
                throw new Error("Buyer doesn't have associated token account of input mint: " + inputMint.toString());
            }
            const buyerPda = ZebecCardService.deriveBuyerPda(buyer, this._programId, buyerCounter);
            const cardPda = ZebecCardService.deriveCardPda(this._programId);
            const cardPdaDecoded = yield this.instructions.program.account.card.fetch(cardPda, "confirmed");
            const zicOwner = cardPdaDecoded.zicOwner;
            const cardVault = cardPdaDecoded.cardVault;
            const revenueVault = cardPdaDecoded.revenueVault;
            const buyerUsdcAta = (0, utils_1.getAssociatedTokenAddressSync)(usdc, buyer);
            const cardVaultUsdcAta = (0, utils_1.getAssociatedTokenAddressSync)(usdc, cardVault, true);
            const revenueVaultUsdcAta = (0, utils_1.getAssociatedTokenAddressSync)(usdc, revenueVault, true);
            const quoteResponse = yield fetch(`https://quote-api.jup.ag/v6/quote?inputMint=${inputMint.toString()}&outputMint=${usdc.toString()}&amount=${parsedInputAmount.toFixed()}&slippageBps=${slippage}&swapMode=ExactOut`);
            const quoteInfojson = yield quoteResponse.json();
            console.debug("quoteResponse:", quoteInfojson);
            if (quoteInfojson.error) {
                throw new Error(quoteInfojson.error);
            }
            // check if buyer has enough balance
            const resAndCtx = yield this._connection.getTokenAccountBalance(buyerInputMintAta, "confirmed");
            const balance = resAndCtx.value.amount;
            if (balance === "" || (0, bignumber_js_1.default)(balance).lt(quoteInfojson.inAmount)) {
                throw new Error("Buyer doesn't have enough balance");
            }
            // get serialized transactions for the swap
            const { swapTransaction } = yield (yield fetch("https://quote-api.jup.ag/v6/swap", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    // quoteResponse from /quote api
                    quoteResponse: quoteInfojson,
                    // user public key to be used for the swap
                    userPublicKey: buyer.toString(),
                    // auto wrap and unwrap SOL. default is true
                    wrapAndUnwrapSol: true,
                    // feeAccount is optional. Use if you want to charge a fee.  feeBps must have been passed in /quote API.
                    // feeAccount: "fee_account_public_key"
                }),
            })).json();
            // deserialize the transaction
            const swapTransactionBuf = buffer_1.Buffer.from(swapTransaction, "base64");
            let transaction = web3_js_1.VersionedTransaction.deserialize(swapTransactionBuf);
            // get address lookup table accounts
            const addressLookupTableAccounts = yield Promise.all(transaction.message.addressTableLookups.map((lookup) => __awaiter(this, void 0, void 0, function* () {
                const data = yield this._connection
                    .getAccountInfo(lookup.accountKey)
                    .then((res) => res.data);
                return new web3_js_1.AddressLookupTableAccount({
                    key: lookup.accountKey,
                    state: web3_js_1.AddressLookupTableAccount.deserialize(data),
                });
            })));
            // decompile transaction message and add transfer instruction
            let message = web3_js_1.TransactionMessage.decompile(transaction.message, {
                addressLookupTableAccounts: addressLookupTableAccounts,
            });
            const amount = new anchor_1.BN(quoteInfojson.outAmount);
            const depositInstruction = yield this.instructions.getDepositToBuyCardInstruction(buyer, buyerPda, buyerUsdcAta, cardPda, cardVault, cardVaultUsdcAta, revenueVault, revenueVaultUsdcAta, usdc, zicOwner, {
                amount,
                cardType,
                index: new anchor_1.BN(buyerCounter),
                tokenType: tokenType,
            });
            message.instructions.push(depositInstruction);
            const { blockhash, lastValidBlockHeight } = yield this._connection.getLatestBlockhash();
            message.recentBlockhash = blockhash;
            // compile the message and update the transaction
            transaction.message = message.compileToV0Message(addressLookupTableAccounts);
            const errorMap = new Map();
            this.instructions.program.idl.errors.forEach((error) => errorMap.set(error.code, error.msg));
            return new payload_1.TransactionPayload(this._connection, errorMap, blockhash, lastValidBlockHeight, transaction, [], this._signTransaction);
        });
    }
    /**
     * Create transaction payload that deposit given USDC amount to card vault and revenue vault.
     * @param params SwapAndDepositParams
     * @example
     *
     * ```
     * const buyerCounter = await service.getNextBuyerCounter();
     * const params: DepositParams = {
     *     buyerAddress: provider.publicKey.toString(),
     *	   buyerCounter,
     * 	   cardType: "master card",
     *	   amount: 100,
     *	   tokenType: "stable",
     * };
     * const payload = await service.deposit(params);
     * const signature = await service.execute({ commitment: "finalized" });
     * ```
     */
    deposit(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const { buyerAddress, buyerCounter, cardType, amount, tokenType, mintAddress } = params;
            const buyer = new web3_js_1.PublicKey(buyerAddress);
            const usdc = new web3_js_1.PublicKey(mintAddress);
            const buyerPda = ZebecCardService.deriveBuyerPda(buyer, this._programId, buyerCounter);
            console.debug("buyerPda:", buyerPda.toString());
            const cardPda = ZebecCardService.deriveCardPda(this._programId);
            const cardPdaDecoded = yield this.instructions.program.account.card.fetch(cardPda, "confirmed");
            const zicOwner = cardPdaDecoded.zicOwner;
            const cardVault = cardPdaDecoded.cardVault;
            const revenueVault = cardPdaDecoded.revenueVault;
            const buyerUsdcAta = (0, utils_1.getAssociatedTokenAddressSync)(usdc, buyer);
            const cardVaultUsdcAta = (0, utils_1.getAssociatedTokenAddressSync)(usdc, cardVault, true);
            const revenueVaultUsdcAta = (0, utils_1.getAssociatedTokenAddressSync)(usdc, revenueVault, true);
            const parsedAmount = (0, bignumber_js_1.default)(amount).times(constants_1.TEN_BIGNUM.pow(constants_1.USDC_DECIMALS)).toFixed();
            const buyerUSDCAtaInfo = yield this._connection.getAccountInfo(buyerUsdcAta, "confirmed");
            if (!buyerUSDCAtaInfo) {
                throw new Error("Buyer doesn't have associated token account of token mint: " + usdc.toString());
            }
            const resAndCtx = yield this._connection.getTokenAccountBalance(buyerUsdcAta, "confirmed");
            const balance = resAndCtx.value.amount;
            if (amount === "" || (0, bignumber_js_1.default)(balance).lt(parsedAmount)) {
                throw new Error("Buyer doesn't have enough balance");
            }
            const ix = yield this.instructions.getDepositToBuyCardInstruction(buyer, buyerPda, buyerUsdcAta, cardPda, cardVault, cardVaultUsdcAta, revenueVault, revenueVaultUsdcAta, usdc, zicOwner, {
                amount: new anchor_1.BN(parsedAmount),
                cardType,
                index: new anchor_1.BN(buyerCounter),
                tokenType: tokenType,
            });
            const payload = yield this._createPayload(buyer, [ix], []);
            return payload;
        });
    }
    /**
     * Fetch quote info given input mint and amount
     * @param GetQuoteInfoParams
     * @returns
     */
    getQuoteInfo(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const { inputAmount, inputMintAddress, outputMintAddress, slippagePercent } = params;
            const inputMint = new web3_js_1.PublicKey(inputMintAddress);
            const outputMint = new web3_js_1.PublicKey(outputMintAddress);
            const inputAmt = (0, bignumber_js_1.default)(inputAmount);
            const parsedInputAmount = inputAmt.times(constants_1.TEN_BIGNUM.pow(constants_1.USDC_DECIMALS));
            const slippage = (0, utils_1.percentToBps)(slippagePercent);
            const quoteResponse = yield fetch(`https://quote-api.jup.ag/v6/quote?inputMint=${inputMint.toString()}&outputMint=${outputMint.toString()}&amount=${parsedInputAmount.toFixed()}&slippageBps=${slippage}&swapMode=ExactOut`);
            const quoteInfojson = yield quoteResponse.json();
            // console.log("quoteResponse:", quoteResponse);
            const quoteInfo = yield (0, utils_1.parseQuoteInfo)(this._connection, quoteInfojson);
            return quoteInfo;
        });
    }
    /**
     * Get next buyer counter that'll be used to derive unique buyerPda
     * @returns
     */
    getNextBuyerCounter() {
        return __awaiter(this, void 0, void 0, function* () {
            const cardPda = ZebecCardService.deriveCardPda(this._programId);
            const decoded = yield this.instructions.program.account.card.fetch(cardPda, "confirmed");
            return decoded.index.addn(1).toString();
        });
    }
    /**
     * Fetch card config data
     * @returns
     */
    getCardConfigInfo() {
        return __awaiter(this, void 0, void 0, function* () {
            const cardPda = ZebecCardService.deriveCardPda(this._programId);
            const decoded = yield this.instructions.program.account.card.fetch(cardPda, "confirmed");
            return {
                index: decoded.index.toString(),
                revenueFee: decoded.revenueFee,
                solFee: decoded.solFee,
                tokenFee: decoded.tokenFee,
                usdcMint: decoded.usdcMint.toString(),
                zicOwner: decoded.zicOwner.toString(),
                cardVault: decoded.cardVault.toString(),
                revenueVault: decoded.revenueVault.toString(),
            };
        });
    }
    /**
     * Fetch buyer pda info
     * @param buyerPda
     * @returns
     */
    getBuyerPdaInfo(buyerPda) {
        return __awaiter(this, void 0, void 0, function* () {
            const decoded = yield this.instructions.program.account.buyer.fetch(buyerPda, "confirmed");
            const amount = (0, bignumber_js_1.default)(decoded.amount.toString())
                .div(constants_1.TEN_BIGNUM.pow(constants_1.USDC_DECIMALS))
                .toFixed();
            return {
                amount,
                buyerAddress: decoded.buyerAddress.toString(),
                cardType: decoded.cardType,
                index: decoded.index.toString(),
                tokenType: decoded.tokenType,
            };
        });
    }
    /**
     * Add event listener for deposit event.
     * @param callback A function that utilize event, slot and signature
     * @example
     * ```
     * const listener = service.addDepositEventLister((event, slot, signature) => {
     *		const index = event.index;
     *		const buyer = event.from;
     *		const buyerPda = ZebecCardService.deriveBuyerPda(buyer, program.programId, index).toString();
     *		const receiver = event.to.toString();
     *		const amount = event.amount;
     *		const data = {
     *			index,
     *			signature,
     *			slot,
     *			buyer,
     *			buyerPda,
     *			receiver,
     *			amount,
     *			cardType: event.cardType,
     *			tokenType: event.tokenType,
     *		};
     *		console.log("event info", data);
     * });
     * ```
     */
    addDepositEventLister(callback) {
        const eventListener = this.instructions.program.addEventListener("DepositEvent", (event, slot, signature) => {
            const parsedEvent = (0, utils_1.parseDepositEvent)(event);
            callback(parsedEvent, slot, signature);
        });
        return eventListener;
    }
    /**
     * Removes event listener
     * @param eventListener
     */
    removeEventLister(eventListener) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.instructions.program.removeEventListener(eventListener);
        });
    }
}
exports.ZebecCardService = ZebecCardService;
